import { useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';

import { Iconify } from 'src/components/iconify';
import { paths } from 'src/routes/paths';
import {
  useBrandKnowledgeControllerListEntries,
  useBrandKnowledgeControllerReviewQueue,
  useBrandKnowledgeControllerListDocuments,
  useBrandKnowledgeControllerCreateEntry,
  useBrandKnowledgeControllerDeleteEntry,
  useBrandKnowledgeControllerConfirmEntry,
  useBrandKnowledgeControllerRejectEntry,
  useBrandKnowledgeControllerSupersede,
  useBrandKnowledgeControllerPresignDocument,
  useBrandKnowledgeControllerFinalizeDocument,
  useBrandKnowledgeControllerRetryDocument,
  useBrandKnowledgeControllerDeleteDocument,
  useBrandKnowledgeControllerReindex,
  useBrandKnowledgeControllerAnswerQuestion,
  useBrandKnowledgeControllerDismissQuestion,
  useBrandKnowledgeControllerListBrandUpdates,
  useBrandKnowledgeControllerConfirmBrandUpdate,
  useBrandKnowledgeControllerRejectBrandUpdate,
  useBrandKnowledgeControllerConfirmAllBrandUpdates,
} from '@/apis/backend/generated/brand-knowledge/brand-knowledge';
import type { BrandAssetProposalDto } from '@/apis/backend/generated/models/brandAssetProposalDto';
import { CreateKnowledgeEntryDtoType } from '@/apis/backend/generated/models/createKnowledgeEntryDtoType';
import type { KnowledgeEntryDto } from '@/apis/backend/generated/models/knowledgeEntryDto';
import type { KnowledgeEvidenceDto } from '@/apis/backend/generated/models/knowledgeEvidenceDto';
import type { BrandKnowledgeQuestionDto } from '@/apis/backend/generated/models/brandKnowledgeQuestionDto';
import type { BrandDocumentDto } from '@/apis/backend/generated/models/brandDocumentDto';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/settings/client/brand/$clientId/knowledge',
)({
  component: BrandKnowledgePage,
});

const ENTRY_TYPES = Object.values(CreateKnowledgeEntryDtoType);
const ACCEPT_DOCS = '.pdf,.docx,.txt,.md';
// Mirror of the server-side MAX_DOC_BYTES so we can fail fast with a friendly message.
const MAX_DOC_BYTES = 300 * 1024 * 1024;

// PUT a file straight to a presigned S3 URL. This is a direct-to-S3 request, not
// a call to our API, so it must NOT carry our Authorization header — XHR is used
// (rather than the generated client) to surface upload progress.
function putFileToPresignedUrl(
  url: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

function BrandKnowledgePage() {
  const navigate = useNavigate();
  const { orgId, clientId } = Route.useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);

  const invalidate = () =>
    queryClient.invalidateQueries({
      predicate: (q) =>
        typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).includes('/brand-knowledge/'),
    });

  const enabled = !!orgId && !!clientId;
  const reviewQuery = useBrandKnowledgeControllerReviewQueue(orgId, clientId, {
    // Proposed learnings + brand updates are created by the background ingest a
    // little after a document finishes, so poll while the Review tab is open.
    query: { enabled, refetchInterval: tab === 0 ? 5000 : false },
  });
  const entriesQuery = useBrandKnowledgeControllerListEntries(
    orgId,
    clientId,
    { status: 'active' },
    { query: { enabled } },
  );
  const documentsQuery = useBrandKnowledgeControllerListDocuments(orgId, clientId, {
    query: {
      enabled,
      // Poll while any document is still parsing so the row flips to
      // Indexed/Failed on its own instead of sitting on "Processing…".
      refetchInterval: (query) => {
        const docs = query.state.data?.data ?? [];
        const inFlight = docs.some((d) => {
          const s = d.status ?? 'uploaded';
          return s !== 'ready' && s !== 'failed';
        });
        return inFlight ? 4000 : false;
      },
    },
  });

  const proposed = reviewQuery.data?.proposedEntries ?? [];
  const questions = reviewQuery.data?.openQuestions ?? [];
  const reviewCount = proposed.length + questions.length;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Button
            size="small"
            startIcon={<Iconify icon="eva:arrow-back-fill" width={16} />}
            onClick={() =>
              navigate({ to: paths.dashboard.orgSettings(orgId).client.brandProfiles })
            }
            sx={{ textTransform: 'none', mb: 1 }}
          >
            All clients
          </Button>
          <Typography variant="h4" gutterBottom>
            Brand knowledge
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The brand brain: rules, facts, and documents the AI grounds every email in. Confirm
            what it learns, and it gets smarter over time.
          </Typography>
        </Box>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab
          label={
            <Stack direction="row" alignItems="center" gap={1}>
              Review
              {reviewCount > 0 && <Chip size="small" color="primary" label={reviewCount} />}
            </Stack>
          }
        />
        <Tab label="Knowledge" />
        <Tab label="Documents" />
      </Tabs>

      {tab === 0 && (
        <ReviewTab
          orgId={orgId}
          clientId={clientId}
          proposed={proposed}
          questions={questions}
          // The active set, so a proposal that contradicts one can name it and
          // offer to replace it rather than only warning about it.
          active={entriesQuery.data?.data ?? []}
          loading={reviewQuery.isLoading}
          onChanged={invalidate}
        />
      )}
      {tab === 1 && (
        <KnowledgeTab
          orgId={orgId}
          clientId={clientId}
          entries={entriesQuery.data?.data ?? []}
          loading={entriesQuery.isLoading}
          onChanged={invalidate}
        />
      )}
      {tab === 2 && (
        <DocumentsTab
          orgId={orgId}
          clientId={clientId}
          documents={documentsQuery.data?.data ?? []}
          loading={documentsQuery.isLoading}
          onChanged={invalidate}
        />
      )}
    </Box>
  );
}

// --------------------------------------------------------------------------
// Review tab — confirm/reject proposed entries; answer/dismiss questions
// --------------------------------------------------------------------------

function ReviewTab(props: {
  orgId: string;
  clientId: string;
  proposed: KnowledgeEntryDto[];
  questions: BrandKnowledgeQuestionDto[];
  active: KnowledgeEntryDto[];
  loading: boolean;
  onChanged: () => void;
}) {
  const { orgId, clientId, proposed, questions, active, loading, onChanged } = props;
  const [answering, setAnswering] = useState<BrandKnowledgeQuestionDto | null>(null);

  const confirm = useBrandKnowledgeControllerConfirmEntry({
    mutation: {
      onSuccess: () => {
        toast.success('Added to brand knowledge');
        onChanged();
      },
      onError: () => toast.error('Could not confirm'),
    },
  });
  // Resolving a contradiction, which confirm cannot do. Confirming a proposal
  // that contradicts a live rule leaves both in play, and an agent handed two
  // rules that disagree follows whichever it reads last.
  const supersede = useBrandKnowledgeControllerSupersede({
    mutation: {
      onSuccess: () => {
        toast.success('Replaced the rule it contradicted');
        onChanged();
      },
      onError: () => toast.error('Could not replace'),
    },
  });

  const reject = useBrandKnowledgeControllerRejectEntry({
    mutation: {
      onSuccess: () => {
        toast.success('Dismissed');
        onChanged();
      },
      onError: () => toast.error('Could not dismiss'),
    },
  });
  const dismissQ = useBrandKnowledgeControllerDismissQuestion({
    mutation: {
      onSuccess: () => onChanged(),
      onError: () => toast.error('Could not dismiss'),
    },
  });

  const updatesQuery = useBrandKnowledgeControllerListBrandUpdates(orgId, clientId, {
    // Proposals land shortly after a deck finishes; poll so they appear without
    // a manual refresh. This query only mounts while the Review tab is active.
    query: { enabled: !!orgId && !!clientId, refetchInterval: 5000 },
  });
  const brandUpdates = updatesQuery.data?.data ?? [];
  const confirmUpdate = useBrandKnowledgeControllerConfirmBrandUpdate({
    mutation: {
      onSuccess: () => {
        toast.success('Applied to brand');
        onChanged();
      },
      onError: () => toast.error('Could not apply'),
    },
  });
  const rejectUpdate = useBrandKnowledgeControllerRejectBrandUpdate({
    mutation: {
      onSuccess: () => onChanged(),
      onError: () => toast.error('Could not reject'),
    },
  });
  const confirmAllUpdates = useBrandKnowledgeControllerConfirmAllBrandUpdates({
    mutation: {
      onSuccess: () => {
        toast.success('Applied all brand updates');
        onChanged();
      },
      onError: () => toast.error('Could not apply all'),
    },
  });
  const updatesBusy = confirmUpdate.isPending || rejectUpdate.isPending || confirmAllUpdates.isPending;

  if (loading) return <Loading />;
  if (proposed.length === 0 && questions.length === 0 && brandUpdates.length === 0) {
    return (
      <Empty
        icon="solar:check-circle-bold-duotone"
        title="Nothing to review"
        body="Proposed learnings and questions will show up here as the AI reads your documents and your sends."
      />
    );
  }

  return (
    <Stack spacing={3}>
      {questions.length > 0 && (
        <Section title="Questions">
          <Stack divider={<Divider />}>
            {questions.map((q) => (
              <Row key={q.id}>
                <Typography variant="body2" sx={{ flex: 1, pr: 2 }}>
                  {q.question}
                </Typography>
                <Stack direction="row" gap={1}>
                  <Button size="small" variant="contained" onClick={() => setAnswering(q)}>
                    Answer
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={dismissQ.isPending}
                    onClick={() => dismissQ.mutate({ orgId, clientId, questionId: q.id })}
                  >
                    Dismiss
                  </Button>
                </Stack>
              </Row>
            ))}
          </Stack>
        </Section>
      )}

      {brandUpdates.length > 0 && (
        <Section title="Proposed brand updates">
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={updatesBusy}
              onClick={() => confirmAllUpdates.mutate({ orgId, clientId })}
              sx={{ textTransform: 'none' }}
            >
              Confirm all
            </Button>
          </Stack>
          <Stack divider={<Divider />}>
            {brandUpdates.map((p) => (
              <Row key={p.id}>
                <Box sx={{ flex: 1, pr: 2 }}>
                  <BrandUpdateContent p={p} />
                </Box>
                <Stack direction="row" gap={1}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={updatesBusy}
                    onClick={() => confirmUpdate.mutate({ orgId, clientId, proposalId: p.id })}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={updatesBusy}
                    onClick={() => rejectUpdate.mutate({ orgId, clientId, proposalId: p.id })}
                  >
                    Reject
                  </Button>
                </Stack>
              </Row>
            ))}
          </Stack>
        </Section>
      )}

      {proposed.length > 0 && (
        <Section title="Proposed learnings">
          <Stack divider={<Divider />}>
            {proposed.map((e) => (
              <Row key={e.id}>
                <Box sx={{ flex: 1, pr: 2 }}>
                  <TypeChip type={e.type} source={e.source} />
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {e.statement}
                  </Typography>
                  <Evidence
                    evidence={e.evidence}
                    conflictsWith={conflictFor(e, active)}
                  />
                </Box>
                <Stack direction="row" gap={1}>
                  {conflictFor(e, active) ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      disabled={supersede.isPending}
                      onClick={() =>
                        supersede.mutate({
                          orgId,
                          clientId,
                          newId: e.id,
                          oldId: conflictFor(e, active)!.id,
                        })
                      }
                    >
                      Replace
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={confirm.isPending}
                      onClick={() => confirm.mutate({ orgId, clientId, entryId: e.id })}
                    >
                      Confirm
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="inherit"
                    disabled={reject.isPending}
                    onClick={() => reject.mutate({ orgId, clientId, entryId: e.id })}
                  >
                    Reject
                  </Button>
                </Stack>
              </Row>
            ))}
          </Stack>
        </Section>
      )}

      <AnswerDialog
        orgId={orgId}
        clientId={clientId}
        question={answering}
        onClose={() => setAnswering(null)}
        onAnswered={() => {
          setAnswering(null);
          onChanged();
        }}
      />
    </Stack>
  );
}

function AnswerDialog(props: {
  orgId: string;
  clientId: string;
  question: BrandKnowledgeQuestionDto | null;
  onClose: () => void;
  onAnswered: () => void;
}) {
  const { orgId, clientId, question, onClose, onAnswered } = props;
  const [answer, setAnswer] = useState('');
  const answerMutation = useBrandKnowledgeControllerAnswerQuestion({
    mutation: {
      onSuccess: () => {
        toast.success('Saved to brand knowledge');
        setAnswer('');
        onAnswered();
      },
      onError: () => toast.error('Could not save answer'),
    },
  });

  return (
    <Dialog open={!!question} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{question?.question}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          placeholder="Your answer…"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!answer.trim() || answerMutation.isPending}
          onClick={() =>
            question &&
            answerMutation.mutate({
              orgId,
              clientId,
              questionId: question.id,
              data: { answerText: answer.trim() },
            })
          }
        >
          {answerMutation.isPending ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --------------------------------------------------------------------------
// Knowledge tab — active entries; add / delete
// --------------------------------------------------------------------------

function KnowledgeTab(props: {
  orgId: string;
  clientId: string;
  entries: KnowledgeEntryDto[];
  loading: boolean;
  onChanged: () => void;
}) {
  const { orgId, clientId, entries, loading, onChanged } = props;
  const [addOpen, setAddOpen] = useState(false);

  const del = useBrandKnowledgeControllerDeleteEntry({
    mutation: {
      onSuccess: () => {
        toast.success('Deleted');
        onChanged();
      },
      onError: () => toast.error('Could not delete'),
    },
  });

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {entries.length} active {entries.length === 1 ? 'item' : 'items'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" width={18} />}
          sx={{ textTransform: 'none', borderRadius: 2 }}
          onClick={() => setAddOpen(true)}
        >
          Add knowledge
        </Button>
      </Stack>

      {loading ? (
        <Loading />
      ) : entries.length === 0 ? (
        <Empty
          icon="solar:lightbulb-bold-duotone"
          title="No brand knowledge yet"
          body="Add a rule or fact, or upload a brand document — the AI will use it in every email."
        />
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Stack divider={<Divider />}>
              {entries.map((e) => (
                <Row key={e.id}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <TypeChip type={e.type} source={e.source} confidence={e.confidence} />
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {e.statement}
                    </Typography>
                  </Box>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      disabled={del.isPending}
                      onClick={() => del.mutate({ orgId, clientId, entryId: e.id })}
                    >
                      <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                    </IconButton>
                  </Tooltip>
                </Row>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <AddEntryDialog
        orgId={orgId}
        clientId={clientId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false);
          onChanged();
        }}
      />
    </>
  );
}

function AddEntryDialog(props: {
  orgId: string;
  clientId: string;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { orgId, clientId, open, onClose, onCreated } = props;
  const [type, setType] = useState<CreateKnowledgeEntryDtoType>(CreateKnowledgeEntryDtoType.rule);
  const [statement, setStatement] = useState('');
  const [tags, setTags] = useState('');

  const create = useBrandKnowledgeControllerCreateEntry({
    mutation: {
      onSuccess: () => {
        toast.success('Added to brand knowledge');
        setStatement('');
        setTags('');
        onCreated();
      },
      onError: () => toast.error('Could not add'),
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add brand knowledge</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField select label="Type" value={type} onChange={(e) => setType(e.target.value as CreateKnowledgeEntryDtoType)}>
            {ENTRY_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t.replace('_', ' ')}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Statement"
            placeholder="e.g. Never use the word 'cheap'; say 'accessible'."
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
          />
          <TextField
            fullWidth
            label="Tags (optional, comma-separated)"
            placeholder="voice, pricing"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!statement.trim() || create.isPending}
          onClick={() =>
            create.mutate({
              orgId,
              clientId,
              data: {
                type,
                statement: statement.trim(),
                tags: tags
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean),
              },
            })
          }
        >
          {create.isPending ? <CircularProgress size={18} color="inherit" /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --------------------------------------------------------------------------
// Documents tab — upload / list / delete + reindex
// --------------------------------------------------------------------------

function DocumentsTab(props: {
  orgId: string;
  clientId: string;
  documents: BrandDocumentDto[];
  loading: boolean;
  onChanged: () => void;
}) {
  const { orgId, clientId, documents, loading, onChanged } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const presign = useBrandKnowledgeControllerPresignDocument();
  const finalize = useBrandKnowledgeControllerFinalizeDocument();
  const retry = useBrandKnowledgeControllerRetryDocument({
    mutation: {
      onSuccess: () => {
        toast.success('Retrying…');
        onChanged();
      },
      onError: () => toast.error('Could not retry'),
    },
  });
  const del = useBrandKnowledgeControllerDeleteDocument({
    mutation: {
      onSuccess: () => {
        toast.success('Removed');
        onChanged();
      },
      onError: () => toast.error('Could not remove'),
    },
  });

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_DOC_BYTES) {
      toast.error(`File is too large. Max ${Math.round(MAX_DOC_BYTES / (1024 * 1024))}MB.`);
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      // 1. Ask the API for a presigned URL (auth + tenant checks happen here).
      const { uploadUrl, storageKey } = await presign.mutateAsync({
        orgId,
        clientId,
        data: {
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          byteSize: file.size,
        },
      });
      // 2. Upload the bytes straight to S3.
      await putFileToPresignedUrl(uploadUrl, file, setProgress);
      // 3. Register the document and kick off async ingestion.
      await finalize.mutateAsync({
        orgId,
        clientId,
        data: {
          storageKey,
          mimeType: file.type || 'application/octet-stream',
          byteSize: file.size,
          title: file.name,
        },
      });
      toast.success('Uploaded — parsing in the background');
      onChanged();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          PDF, DOCX, TXT or MD. We parse, store, and learn from them.
        </Typography>
        <Button
          variant="contained"
          disabled={uploading}
          startIcon={
            uploading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Iconify icon="eva:cloud-upload-fill" width={18} />
            )
          }
          sx={{ textTransform: 'none', borderRadius: 2 }}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? `Uploading ${progress}%` : 'Upload document'}
        </Button>
        <input ref={fileInputRef} type="file" accept={ACCEPT_DOCS} hidden onChange={onFile} />
      </Stack>

      {loading ? (
        <Loading />
      ) : documents.length === 0 ? (
        <Empty
          icon="solar:document-add-bold-duotone"
          title="No documents yet"
          body="Upload a brand guide, voice doc, or one-pager. The AI reads it and proposes knowledge for you to confirm."
        />
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Stack divider={<Divider />}>
              {documents.map((d) => (
                <Row key={d.id}>
                  <Box sx={{ flex: 1, pr: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Iconify icon="solar:file-text-bold-duotone" width={22} />
                    <Box>
                      <Typography variant="body2">{d.title ?? 'Document'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {docStatusLabel(d)}
                      </Typography>
                    </Box>
                  </Box>
                  {(d.status === 'failed' || d.status === 'ready') && (
                    <Tooltip title={d.status === 'failed' ? 'Retry' : 'Reprocess'}>
                      <IconButton
                        size="small"
                        disabled={retry.isPending}
                        onClick={() => retry.mutate({ orgId, clientId, documentId: d.id })}
                      >
                        <Iconify icon="solar:refresh-bold" width={18} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      disabled={del.isPending}
                      onClick={() => del.mutate({ orgId, clientId, documentId: d.id })}
                    >
                      <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                    </IconButton>
                  </Tooltip>
                </Row>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </>
  );
}

// --------------------------------------------------------------------------
// Small shared UI bits
// --------------------------------------------------------------------------

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {props.title}
      </Typography>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>{props.children}</CardContent>
      </Card>
    </Box>
  );
}

function Row(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {props.children}
    </Box>
  );
}

/**
 * Why this was proposed, for the person deciding whether it is true.
 *
 * Confirming a rule sends it into every future email for this brand, and until
 * now the card offered the sentence and nothing behind it. "The logo always
 * carries clear space" is either a standing rule or one art director's note
 * about one hero, and the only thing that tells them apart is what was actually
 * said. Renders nothing for an entry with no evidence, which is every entry a
 * person typed themselves.
 */
function Evidence({
  evidence,
  conflictsWith,
}: {
  evidence?: KnowledgeEvidenceDto;
  conflictsWith?: KnowledgeEntryDto | null;
}) {
  if (!evidence) return null;

  // Whose round it was, when the entry records it. The lane is the difference
  // between the customer stating a preference and our own reviewer setting the
  // bar, and both are worth confirming for different reasons -- so the line
  // names it rather than leaving "Round 2" to be read as the customer by
  // default. Entries written before the internal lane was read carry no kind,
  // and those keep the bare round they have always shown.
  const round = roundLabel(evidence);

  if (!evidence.excerpt && !round && !evidence.conflictsWithEntryId) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {evidence.excerpt && (
        <Box
          sx={{
            borderLeft: 2,
            borderColor: 'divider',
            pl: 1.5,
            py: 0.25,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
            {evidence.excerpt}
          </Typography>
        </Box>
      )}
      {round && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {round}
        </Typography>
      )}
      {evidence.conflictsWithEntryId && (
        <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>
          <Iconify
            icon="solar:danger-triangle-bold"
            width={14}
            sx={{ color: 'warning.main', mt: 0.25, flexShrink: 0 }}
          />
          <Typography variant="caption" color="warning.main">
            {conflictsWith
              ? `Contradicts a rule already in use: "${conflictsWith.statement}" Replace swaps that one out for this.`
              : 'Contradicts a rule already in use. Confirming this leaves both in play.'}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

/**
 * Which round this came out of, and whose it was.
 *
 * The lane is the difference between the customer stating a preference and our
 * own reviewer setting the bar. Both are worth confirming, for different
 * reasons, so the line names it rather than leaving a bare "Round 2" to be read
 * as the customer by default. Entries written before the internal lane was read
 * carry no kind and keep the bare round they have always shown.
 */
function roundLabel(evidence: KnowledgeEvidenceDto): string | null {
  if (!evidence.ticketId || typeof evidence.roundNumber !== 'number') return null;
  if (evidence.roundKind === 'internal') return `Internal review, round ${evidence.roundNumber}`;
  if (evidence.roundKind === 'client') return `Client round ${evidence.roundNumber}`;
  return `Round ${evidence.roundNumber}`;
}

/**
 * The live rule a proposal contradicts, when we hold it.
 *
 * The conflict is detected server-side at extraction and the id travels on the
 * entry; this only resolves it to something a person can read. Null when the id
 * points at a rule that is no longer active, in which case there is nothing to
 * replace and confirming is the ordinary path.
 */
function conflictFor(
  entry: KnowledgeEntryDto,
  active: KnowledgeEntryDto[]
): KnowledgeEntryDto | null {
  const id = entry.evidence?.conflictsWithEntryId;
  if (!id) return null;
  return active.find((candidate) => candidate.id === id) ?? null;
}

function TypeChip(props: { type: string; source: string; confidence?: number }) {
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Chip size="small" label={props.type.replace('_', ' ')} />
      <Typography variant="caption" color="text.secondary">
        {sourceLabel(props.source)}
        {typeof props.confidence === 'number' && ` · ${Math.round(props.confidence * 100)}%`}
      </Typography>
    </Stack>
  );
}

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

function Empty(props: { icon: string; title: string; body: string }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ textAlign: 'center', py: 6 }}>
        <Iconify icon={props.icon} width={48} sx={{ color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1">{props.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', mt: 0.5 }}>
          {props.body}
        </Typography>
      </CardContent>
    </Card>
  );
}

function sourceLabel(source: string): string {
  switch (source) {
    case 'uploaded_document':
      return 'from document';
    case 'extracted_site':
      return 'from website';
    case 'learned_feedback':
      return 'learned';
    case 'wg_round_feedback':
      return 'from White Glove feedback';
    case 'audit_insight':
      return 'from an audit';
    case 'answered_question':
      return 'from your answer';
    case 'manual':
      return 'added by you';
    default:
      return source;
  }
}

function ColorSwatch({ hex }: { hex: string }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: 0.5,
          bgcolor: hex,
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        {hex}
      </Typography>
    </Box>
  );
}

function BrandUpdateContent({ p }: { p: BrandAssetProposalDto }) {
  const changed = p.changeKind === 'changed';
  const header = (text: string) => (
    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
      <Chip size="small" label={text} sx={{ textTransform: 'capitalize' }} />
      <Typography variant="caption" color="text.secondary">
        {changed ? 'update' : 'new'}
      </Typography>
    </Stack>
  );

  switch (p.assetType) {
    case 'color':
      return (
        <Box>
          {header(`${p.usage ?? 'color'} color`)}
          <Stack direction="row" alignItems="center" gap={1}>
            {changed && p.currentHex && (
              <>
                <ColorSwatch hex={p.currentHex} />
                <Iconify icon="eva:arrow-forward-fill" width={16} />
              </>
            )}
            {p.proposedHex && <ColorSwatch hex={p.proposedHex} />}
          </Stack>
        </Box>
      );
    case 'font':
      return (
        <Box>
          {header(`${p.usage ?? 'body'} font`)}
          <Typography variant="body2">
            {changed && p.currentFamily ? `${p.currentFamily} → ` : null}
            <strong>{p.proposedFamily}</strong>
          </Typography>
        </Box>
      );
    case 'logo':
      return (
        <Box>
          {header('logo')}
          {p.previewUrl && (
            <Box
              component="img"
              src={p.previewUrl}
              alt="Proposed logo"
              sx={{
                height: 40,
                maxWidth: 180,
                objectFit: 'contain',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.5,
              }}
            />
          )}
        </Box>
      );
    case 'imagery':
      return (
        <Box>
          {header('imagery')}
          {p.previewUrl && (
            <Box
              component="img"
              src={p.previewUrl}
              alt="Proposed imagery"
              sx={{ height: 56, maxWidth: 100, objectFit: 'cover', borderRadius: 1 }}
            />
          )}
        </Box>
      );
    case 'design_direction':
      return (
        <Box>
          {header('design direction')}
          {p.imageryStyle && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {p.imageryStyle}
            </Typography>
          )}
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {p.keywords.map((k) => (
              <Chip key={k} size="small" variant="outlined" label={k} />
            ))}
          </Stack>
        </Box>
      );
    default:
      return <Typography variant="body2">{p.assetType}</Typography>;
  }
}

function docStatusLabel(d: BrandDocumentDto): string {
  const status = d.status ?? 'uploaded';
  if (status === 'ready') {
    return d.chunkCount ? `Indexed · ${d.chunkCount} sections` : 'Indexed';
  }
  if (status === 'failed') return 'Failed to parse';
  return 'Processing…';
}
